/** @jsxImportSource @emotion/react */
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
  useMemo,
  useState,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { App, UploadFile } from 'antd'
import type { UploadChangeParam } from 'antd/es/upload'
import req from '@sportninja/common/api/request'
import {
  ALLOWED_IMAGE_TYPES,
  MAX_IMAGE_SIZE_MB,
} from '@sportninja/common/constants/imageRequirements'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

// Types
export type TeamData = {
  id?: string
  name_full?: string
  name?: string
  fullName?: string // From parsed team data
  sport_id?: string
  sportId?: string // From parsed team data
  generic_sport_id?: string
  phone?: string
  phone_country?: string
  email?: string
  website?: string
  support_phone?: string
  support_phone_country?: string
  support_email?: string
  support_website?: string
  is_placeholder?: boolean
  isPlaceholder?: boolean // From parsed team data
  sf_id?: string
  is_public?: boolean
  isPublic?: boolean // From parsed team data
  image?: {
    full_path?: string
  }
  imageId?: string // From parsed team data
  background_image?: {
    full_path?: string
  }
  backgroundImageId?: string // From parsed team data
  permission?: {
    delete?: boolean
    update?: boolean
  }
  organization?: {
    id?: string
  }
}

export type Sport = {
  id: string
  name: string
  name_full: string
}

export type GenericSport = {
  id: string
  name: string
}

type CustomError = {
  name_full: string | null
  sport_id: string | null
  phone: string | null
  email: string | null
  website: string | null
  support_phone: string | null
  support_email: string | null
  support_website: string | null
  sf_id: string | null
  [key: string]: string | null
}

type TeamState = {
  // Core fields
  id: string | null
  name_full: string
  sport_id: string | null
  generic_sport_id: string | null
  selectedSport: string | null

  // Contact info
  phone: string
  phone_country: string
  email: string
  website: string
  support_phone: string
  support_phone_country: string
  support_email: string
  support_website: string

  // Images
  image_url: string | null
  image_data: string | null
  image_changed: boolean
  background_image_url: string | null
  background_image_data: string | null
  background_image_changed: boolean

  // Conditional fields
  is_placeholder: boolean
  sf_id: string
  is_public: boolean

  // UI state
  isLoading: boolean
  error: string | null
  fieldErrors: string[]
  customError: CustomError
}

type TeamAction =
  | { type: 'SET_FIELD'; field: keyof TeamState; value: unknown }
  | {
      type: 'SET_SPORT'
      payload: {
        sportId: string | null
        genericSportId: string | null
        selectedSport: string | null
      }
    }
  | { type: 'SET_LOADING'; value: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_FIELD_ERRORS'; errors: string[] }
  | { type: 'SET_CUSTOM_ERROR_BY_FIELD'; field: string; message: string | null }
  | { type: 'CLEAR_CUSTOM_ERROR_BY_FIELD'; field: string }
  | { type: 'CLEAR_ALL_ERRORS' }
  | { type: 'RESET_FORM' }
  | { type: 'INIT_EDIT_MODE'; data: TeamData }
  | {
      type: 'SET_IMAGE'
      payload: { url: string | null; data: string | null; changed: boolean }
    }
  | {
      type: 'SET_BACKGROUND_IMAGE'
      payload: { url: string | null; data: string | null; changed: boolean }
    }

const initialCustomError: CustomError = {
  name_full: null,
  sport_id: null,
  phone: null,
  email: null,
  website: null,
  support_phone: null,
  support_email: null,
  support_website: null,
  sf_id: null,
}

const initialState: TeamState = {
  id: null,
  name_full: '',
  sport_id: null,
  generic_sport_id: null,
  selectedSport: null,
  phone: '',
  phone_country: '',
  email: '',
  website: '',
  support_phone: '',
  support_phone_country: '',
  support_email: '',
  support_website: '',
  image_url: null,
  image_data: null,
  image_changed: false,
  background_image_url: null,
  background_image_data: null,
  background_image_changed: false,
  is_placeholder: false,
  sf_id: '',
  is_public: true,
  isLoading: false,
  error: null,
  fieldErrors: [],
  customError: initialCustomError,
}

function teamReducer(state: TeamState, action: TeamAction): TeamState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value }

    case 'SET_SPORT':
      return {
        ...state,
        sport_id: action.payload.sportId,
        generic_sport_id: action.payload.genericSportId,
        selectedSport: action.payload.selectedSport,
      }

    case 'SET_LOADING':
      return { ...state, isLoading: action.value }

    case 'SET_ERROR':
      return { ...state, error: action.error }

    case 'SET_FIELD_ERRORS':
      return { ...state, fieldErrors: action.errors }

    case 'SET_CUSTOM_ERROR_BY_FIELD':
      return {
        ...state,
        customError: {
          ...state.customError,
          [action.field]: action.message,
        },
        fieldErrors: action.message
          ? [...new Set([...state.fieldErrors, action.field])]
          : state.fieldErrors,
      }

    case 'CLEAR_CUSTOM_ERROR_BY_FIELD':
      return {
        ...state,
        customError: {
          ...state.customError,
          [action.field]: null,
        },
        fieldErrors: state.fieldErrors.filter((e) => e !== action.field),
      }

    case 'CLEAR_ALL_ERRORS':
      return {
        ...state,
        error: null,
        fieldErrors: [],
        customError: initialCustomError,
      }

    case 'SET_IMAGE':
      return {
        ...state,
        image_url: action.payload.url,
        image_data: action.payload.data,
        image_changed: action.payload.changed,
      }

    case 'SET_BACKGROUND_IMAGE':
      return {
        ...state,
        background_image_url: action.payload.url,
        background_image_data: action.payload.data,
        background_image_changed: action.payload.changed,
      }

    case 'RESET_FORM':
      return { ...initialState }

    case 'INIT_EDIT_MODE':
      return {
        ...state,
        id: action.data.id || null,
        name_full:
          action.data.name_full ||
          action.data.fullName ||
          action.data.name ||
          '',
        sport_id: action.data.sport_id || action.data.sportId || null,
        generic_sport_id: action.data.generic_sport_id || null,
        selectedSport:
          action.data.generic_sport_id ||
          action.data.sport_id ||
          action.data.sportId ||
          null,
        phone: action.data.phone || '',
        phone_country:
          action.data.phone_country ||
          (action.data.phone
            ? parsePhoneNumberFromString(action.data.phone)?.country || ''
            : ''),
        email: action.data.email || '',
        website: action.data.website || '',
        support_phone: action.data.support_phone || '',
        support_phone_country:
          action.data.support_phone_country ||
          (action.data.support_phone
            ? parsePhoneNumberFromString(action.data.support_phone)?.country ||
              ''
            : ''),
        support_email: action.data.support_email || '',
        support_website: action.data.support_website || '',
        image_url:
          getImageThumbnailId(action.data) || action.data.imageId || null,
        background_image_url:
          resolveAssetUrl(action.data.background_image) ||
          action.data.backgroundImageId ||
          null,
        is_placeholder:
          action.data.is_placeholder ?? action.data.isPlaceholder ?? false,
        sf_id: action.data.sf_id || '',
        is_public: action.data.is_public ?? action.data.isPublic ?? true,
      }

    default:
      return state
  }
}

// Context type
type TeamContextValue = {
  state: TeamState
  dispatch: React.Dispatch<TeamAction>
  isEdit: boolean
  onSubmit: () => Promise<void>
  onCancel: () => void
  onDeleteSuccess: () => void
  showSuccessMessage: (text: string) => void
  setCustomErrorByField: (field: string, message: string | null) => void
  clearCustomErrorByField: (field: string) => void
  beforeUpload: (file: File) => boolean
  handleChange: (info: UploadChangeParam<UploadFile>) => void
  handleChangeBackground: (info: UploadChangeParam<UploadFile>) => void
  sportOptions: Array<Sport & { is_generic: boolean }>
  genericId: string | null
  activeTab: string
  setActiveTab: React.Dispatch<React.SetStateAction<string>>
  permission: { delete?: boolean; update?: boolean }
}

const TeamContext = createContext<TeamContextValue | null>(null)

// Hook to use team context
export const useTeamContext = () => {
  const context = useContext(TeamContext)
  if (!context) {
    throw new Error('useTeamContext must be used within TeamProvider')
  }
  return context
}

// Provider props
type TeamProviderProps = {
  children: ReactNode
  isOpen: boolean
  onClose: () => void
  initialState?: TeamData
  organizationId: string
  onSuccess: () => void
  onDelete?: () => void
  sports: Sport[]
  genericSports: GenericSport[]
  isParentPublic?: boolean
  permission?: { delete?: boolean; update?: boolean }
  /** See `TeamModalOwnProps.scheduleId` — optional, additive. */
  scheduleId?: string
}

export const TeamProvider: React.FC<TeamProviderProps> = ({
  children,
  onClose,
  initialState: initialTeamData,
  organizationId,
  onSuccess,
  onDelete,
  sports,
  genericSports,
  isParentPublic = true,
  permission = {},
  scheduleId,
}) => {
  const [state, dispatch] = useReducer(teamReducer, initialState)
  const [activeTab, setActiveTab] = useState('0')
  const { message } = App.useApp()
  const queryClient = useQueryClient()

  /**
   * Drops the cached team lists after a team is created, edited or deleted.
   *
   * The add/edit Game modal's Home/Visiting pickers read
   * `queryKeys.scheduleTeams`, which is cached for minutes — without this a
   * team created here would not appear in that list until the cache expired.
   *
   * The whole root is invalidated rather than one competition's entry: a team
   * can be created against a competition this modal was not opened from, and a
   * stale list is worse than one extra fetch.
   */
  const invalidateTeamLists = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.scheduleTeams.all })
  }, [queryClient])
  const isEdit = Boolean(initialTeamData?.id)

  // Build sport options following ScheduleModal pattern
  const sportOptions = useMemo(() => {
    if (!sports || !genericSports) {
      return []
    }
    return [
      ...sports
        .map((option) => ({
          ...option,
          name: option.name_full,
          is_generic: false,
        }))
        .filter((s) => !s.name.toLowerCase().includes('generic')),
      ...genericSports.map((option) => ({
        ...option,
        id: option.id,
        name: option.name,
        name_full: option.name,
        sport_id: sports?.find((sport) => sport.name === 'generic')?.id,
        is_generic: true,
      })),
    ]
  }, [sports, genericSports])

  // Get generic sport base ID
  const genericId = useMemo(() => {
    return sports?.find((sport) => sport.name === 'generic')?.id || null
  }, [sports])

  // Helper functions for error handling
  const setCustomErrorByField = useCallback(
    (field: string, errorMessage: string | null) => {
      dispatch({
        type: 'SET_CUSTOM_ERROR_BY_FIELD',
        field,
        message: errorMessage,
      })
    },
    []
  )

  const clearCustomErrorByField = useCallback((field: string) => {
    dispatch({ type: 'CLEAR_CUSTOM_ERROR_BY_FIELD', field })
  }, [])

  const setCustomErrors = useCallback(
    (invalid_fields: Record<string, string | string[]>) => {
      const normalizeError = (error: string | string[]): string => {
        return Array.isArray(error) ? error.join(', ') : error
      }

      // Track which tab has errors
      const generalFields = ['name_full', 'name', 'sport_id', 'sf_id']
      const contactFields = [
        'phone',
        'email',
        'website',
        'support_phone',
        'support_email',
        'support_website',
      ]
      let hasGeneralError = false
      let hasContactError = false

      Object.entries(invalid_fields).forEach(([key, value]) => {
        const fieldName = key === 'name' ? 'name_full' : key
        setCustomErrorByField(fieldName, normalizeError(value))

        if (generalFields.includes(fieldName)) {
          hasGeneralError = true
        }
        if (contactFields.includes(fieldName)) {
          hasContactError = true
        }
      })

      // Switch to tab with errors (prefer General tab)
      if (hasGeneralError) {
        setActiveTab('0')
      } else if (hasContactError) {
        setActiveTab('1')
      }
    },
    [setCustomErrorByField]
  )

  // Initialize edit mode when data is available
  React.useEffect(() => {
    if (initialTeamData && isEdit) {
      dispatch({
        type: 'INIT_EDIT_MODE',
        data: initialTeamData,
      })
    }
  }, [initialTeamData, isEdit])

  // Image upload helpers
  const getBase64 = (
    img: File,
    callback: (result: string | ArrayBuffer | null) => void
  ) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => callback(reader.result))
    if (img) {
      reader.readAsDataURL(img)
    }
  }

  const beforeUpload = useCallback(
    (file: File): boolean => {
      const isAllowedType = ALLOWED_IMAGE_TYPES.includes(file.type)
      const isLtMaxSize = file.size / 1024 / 1024 <= MAX_IMAGE_SIZE_MB
      if (!isAllowedType) {
        message.error(
          'Please upload a file in one of the following formats: JPEG, GIF, PNG, AVIF, WebP, TIFF, or HEIC.'
        )
        return false
      }
      if (!isLtMaxSize) {
        message.error(
          'File is too large. Please upload an image smaller than 5MB.'
        )
        return false
      }
      return true
    },
    [message]
  )

  const handleChange = useCallback((info: UploadChangeParam<UploadFile>) => {
    if (info.file.originFileObj) {
      getBase64(info.file.originFileObj, (url) => {
        dispatch({
          type: 'SET_IMAGE',
          payload: {
            url: url as string,
            data: url as string,
            changed: true,
          },
        })
      })
    }
  }, [])

  const handleChangeBackground = useCallback(
    (info: UploadChangeParam<UploadFile>) => {
      if (info.file.originFileObj) {
        getBase64(info.file.originFileObj, (url) => {
          dispatch({
            type: 'SET_BACKGROUND_IMAGE',
            payload: {
              url: url as string,
              data: url as string,
              changed: true,
            },
          })
        })
      }
    },
    []
  )

  const onSubmit = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', value: true })
    dispatch({ type: 'CLEAR_ALL_ERRORS' })

    try {
      // Prepare form data
      const formData: Record<string, unknown> = {
        name: state.name_full.trim(),
        name_full: state.name_full.trim(),
        sport_id: state.sport_id,
        generic_sport_id: state.generic_sport_id,
        email: state.email || undefined,
        phone: state.phone || undefined,
        phone_country: state.phone_country || 'US',
        website: state.website || undefined,
        support_email: state.support_email || undefined,
        support_phone: state.support_phone || undefined,
        support_phone_country: state.support_phone_country || 'US',
        support_website: state.support_website || undefined,
        is_placeholder: state.is_placeholder,
        sf_id: state.sf_id || undefined,
        is_public: state.is_public,
      }

      if (isEdit && state.id) {
        // Handle image deletions first
        if (state.image_changed && !state.image_url) {
          await req(`/teams/${state.id}/image`, { method: 'DELETE' })
        }
        if (state.background_image_changed && !state.background_image_url) {
          await req(`/teams/${state.id}/background-image`, { method: 'DELETE' })
        }

        // remove sport_id and generic_sport_id if null to avoid validation errors
        delete formData.sport_id
        delete formData.generic_sport_id

        // Update team
        await req(`/teams/${state.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
        })

        // Upload new images
        if (state.image_changed && state.image_data) {
          await req(`/teams/${state.id}/image`, {
            method: 'POST',
            body: state.image_data,
          })
        }
        if (state.background_image_changed && state.background_image_data) {
          await req(`/teams/${state.id}/background-image`, {
            method: 'POST',
            body: state.background_image_data,
          })
        }

        message.success(`${state.name_full.trim()} updated successfully`, 5)
      } else {
        // Create new team
        let response

        if (scheduleId) {
          // Attach the team to this schedule/division node so it isn't
          // created as an orphan org-level team (see ScheduleTree/index.jsx:318).
          response = await req(`/schedules/${scheduleId}/teams`, {
            method: 'POST',
            body: JSON.stringify({ name: state.name_full.trim() }),
          })
        } else {
          formData.organization_id = organizationId
          if (typeof formData.is_public === 'undefined') {
            formData.is_public = isParentPublic
          }

          response = await req('/teams', {
            method: 'POST',
            body: JSON.stringify(formData),
          })
        }

        // Upload images after team creation
        if (state.image_changed && state.image_data) {
          await req(`/teams/${response.data.id}/image`, {
            method: 'POST',
            body: state.image_data,
          })
        }
        if (state.background_image_changed && state.background_image_data) {
          await req(`/teams/${response.data.id}/background-image`, {
            method: 'POST',
            body: state.background_image_data,
          })
        }

        message.success(`${state.name_full.trim()} created successfully`, 5)
      }

      invalidateTeamLists()
      onSuccess()
      onClose()
    } catch (error: unknown) {
      const err = error as {
        invalid_fields?: Record<string, string | string[]>
        message?: string
      }

      if (err.invalid_fields) {
        setCustomErrors(err.invalid_fields)
      } else {
        const errorMessage = getErrorMessage(error)
        dispatch({ type: 'SET_ERROR', error: errorMessage })
      }
    } finally {
      dispatch({ type: 'SET_LOADING', value: false })
    }
  }, [
    state,
    isEdit,
    organizationId,
    isParentPublic,
    scheduleId,
    onSuccess,
    onClose,
    message,
    setCustomErrors,
  ])

  const onCancel = useCallback(() => {
    dispatch({ type: 'RESET_FORM' })
    setActiveTab('0')
    onClose()
  }, [onClose])

  const onDeleteSuccess = useCallback(() => {
    dispatch({ type: 'RESET_FORM' })
    setActiveTab('0')
    invalidateTeamLists()
    if (onDelete) {
      onDelete()
    } else {
      onSuccess()
    }
    onClose()
  }, [onDelete, onSuccess, onClose, invalidateTeamLists])

  const showSuccessMessage = useCallback(
    (text: string) => {
      message.success(text)
    },
    [message]
  )

  const value: TeamContextValue = {
    state,
    dispatch,
    isEdit,
    onSubmit,
    onCancel,
    onDeleteSuccess,
    showSuccessMessage,
    setCustomErrorByField,
    clearCustomErrorByField,
    beforeUpload,
    handleChange,
    handleChangeBackground,
    sportOptions,
    genericId,
    activeTab,
    setActiveTab,
    permission,
  }

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>
}

export default TeamContext
